/**
 * Device groups API client, one function per route of
 * `app/registry_devices/api/device_groups_controller.py`. Every route checks company ownership, and a
 * group of another company answers 404.
 *
 * Refusals carry an `error_type`: `DEVICE_GROUP_NAME_TAKEN` (409), `DEVICE_GROUP_LIMIT_REACHED` (409),
 * `DEVICE_GROUP_UNKNOWN_DEVICES` (400, `error_data.device_ids`) and
 * `DEVICE_GROUP_DEVICES_IN_OTHER_GROUP` (409, `error_data.devices: DeviceInOtherGroup[]`).
 */

import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  DeviceGroup,
  DeviceGroupCreateRequest,
  DeviceGroupsResponse,
  DeviceGroupUpdateRequest,
} from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";

const groupsUrl = (companyId: number) =>
  `/api/company/${companyId}/device-groups`;

export const deviceGroupsApi = {
  /** The company's groups ordered by name, case-insensitively. */
  list: (companyId: number) =>
    goSolidApi.get<DeviceGroupsResponse>(groupsUrl(companyId)),
  create: (companyId: number, data: DeviceGroupCreateRequest) =>
    goSolidApi.post<DeviceGroup>(groupsUrl(companyId), data),
  /**
   * Renames the group and adds and removes members in one transaction. A device in another group is
   * refused, never moved.
   */
  update: (
    companyId: number,
    groupId: number,
    data: DeviceGroupUpdateRequest
  ) =>
    goSolidApi.patch<DeviceGroup>(`${groupsUrl(companyId)}/${groupId}`, data),
  /** Deletes the group; its devices become ungrouped. */
  remove: (companyId: number, groupId: number) =>
    goSolidApi.delete(`${groupsUrl(companyId)}/${groupId}`),
};
